import { QueryClient } from '@tanstack/react-query';
import { env } from '@/config/env';

/**
 * React Query client configuration
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Stale time: 5 minutes
      staleTime: 5 * 60 * 1000,
      
      // Cache time: 10 minutes
      gcTime: 10 * 60 * 1000,
      
      // Retry failed requests
      retry: (failureCount, error: any) => {
        // Don't retry on 4xx errors
        if (error?.status >= 400 && error?.status < 500) {
          return false;
        }
        // Retry up to 3 times for other errors
        return failureCount < 3;
      },
      
      // Retry delay with exponential backoff
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      
      // Refetch on window focus in production
      refetchOnWindowFocus: env.isProduction,
      
      // Refetch on reconnect
      refetchOnReconnect: true,
    },
    mutations: {
      // Retry failed mutations once
      retry: 1,
      
      // Retry delay
      retryDelay: 1000,
    },
  },
});

/**
 * Query keys for consistent cache management
 */
export const queryKeys = {
  // Ontologies
  ontologies: ['ontologies'] as const,
  ontology: (id: string) => ['ontologies', id] as const,
  
  // Nodes
  nodes: (ontologyId: string) => ['ontologies', ontologyId, 'nodes'] as const,
  node: (ontologyId: string, nodeId: string) => 
    ['ontologies', ontologyId, 'nodes', nodeId] as const,
  
  // Edges
  edges: (ontologyId: string) => ['ontologies', ontologyId, 'edges'] as const,
  edge: (ontologyId: string, edgeId: string) => 
    ['ontologies', ontologyId, 'edges', edgeId] as const,
  
  // Search
  search: (ontologyId: string, query: string) => 
    ['ontologies', ontologyId, 'search', query] as const,
  
  // Similar nodes
  similar: (ontologyId: string, nodeId: string) => 
    ['ontologies', ontologyId, 'nodes', nodeId, 'similar'] as const,
  
  // Versions
  versions: (ontologyId: string) =>
    ['ontologies', ontologyId, 'versions'] as const,

  // Health
  health: ['health'] as const,

  // Knowledge Bases
  knowledgeBases: ['knowledge-bases'] as const,
  knowledgeBase: (id: string) => ['knowledge-bases', id] as const,
  kbDocuments: (kbId: string) => ['knowledge-bases', kbId, 'documents'] as const,
  kbDocument: (id: string) => ['documents', id] as const,
  kbDocumentMarkdown: (id: string) => ['documents', id, 'markdown'] as const,
  /** Knowledge units (chunks) of a single knowledge object — only loaded when the row is expanded. */
  knowledgeUnits: (knowledgeObjectId: string) =>
    ['knowledge-objects', knowledgeObjectId, 'units'] as const,
  /** L2 summaries + L3 extractions for a knowledge object's units. */
  knowledgeUnitLayers: (knowledgeObjectId: string) =>
    ['knowledge-objects', knowledgeObjectId, 'layers'] as const,
} as const;


// Made with Bob
